import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CustomersPageService,
  type CustomerListRow,
  type CustomersPageQuery,
  type CustomersSummary,
  type PageResponse,
} from "@/services/customers-page.service";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

export function useCustomersRowsPage(query: CustomersPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<CustomerListRow>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    void CustomersPageService.getCustomersPage(query).then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [
    query.enabled,
    query.page,
    query.pageSize,
    query.search,
    query.view,
    query.city,
    query.branch,
    query.region,
    query.sortField,
    query.sortDir,
    query.refreshToken,
  ]);

  return { ...state, loading };
}

export function useCustomersSummary(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<CustomersSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    void CustomersPageService.getSummary(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useCustomersServerPaging(query: Omit<CustomersPageQuery, "refreshToken"> & { enabled?: boolean }) {
  const [refreshToken, setRefreshToken] = useState(0);
  const page = useCustomersRowsPage({
    ...query,
    enabled: query.enabled,
    refreshToken,
  });
  const summary = useCustomersSummary({
    enabled: query.enabled,
    refreshToken,
  });

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  return useMemo(() => ({
    rows: page.data,
    count: page.count,
    loading: page.loading || summary.loading,
    error: page.error ?? summary.error,
    summary: summary.data,
    refresh,
  }), [page.data, page.count, page.loading, page.error, summary.loading, summary.error, summary.data, refresh]);
}
